import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  SkipNext as SkipIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { ManageableSession } from './types';

interface QuestionnaireQuestion {
  id: number;
  question_text: string;
  question_type: string;
}

interface FeedbackPayload {
  is_skipped?: boolean;
  submitted_at?: string | null;
  responses?: Record<string, string | string[] | number | null>;
  questionnaire?: {
    title?: string;
    description?: string;
    questions?: QuestionnaireQuestion[];
  };
}

interface SessionFeedbackDialogProps {
  open: boolean;
  session: ManageableSession | null;
  onClose: () => void;
}

function StarRating({ score, maxScore = 5 }: { score: number; maxScore?: number }) {
  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      {Array.from({ length: maxScore }, (_, i) =>
        i < score ? (
          <StarIcon key={i} sx={{ fontSize: 18, color: 'warning.main' }} />
        ) : (
          <StarBorderIcon key={i} sx={{ fontSize: 18, color: 'grey.400' }} />
        ),
      )}
    </Stack>
  );
}

function formatSubmittedAt(value?: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Post-interview questionnaire responses — same data company admins see via
 * FeedbackModal on Interview Overview. Separate from the AI assessment report.
 */
const SessionFeedbackDialog: React.FC<SessionFeedbackDialogProps> = ({
  open,
  session,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FeedbackPayload | null>(null);

  const sessionId = session?.id ?? null;

  const fetchFeedback = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const response = await api.get(
        `/feedback-questionnaires/responses/${sessionId}/`,
      );
      setData(response.data || null);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(
        e.response?.data?.detail
          || e.message
          || 'Failed to load post-interview feedback.',
      );
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!open || !sessionId) {
      setData(null);
      setError(null);
      return;
    }
    fetchFeedback();
  }, [open, sessionId, fetchFeedback]);

  const candidateLabel =
    session?.candidate_name
    || session?.candidate_email
    || (session ? `Session #${session.id}` : '');

  const submittedLabel = formatSubmittedAt(data?.submitted_at);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: 'success.main',
              color: 'success.contrastText',
              display: 'flex',
            }}
          >
            <AssignmentIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              Post-Interview Feedback
            </Typography>
            {candidateLabel && (
              <Typography variant="caption" color="text.secondary">
                {candidateLabel}
                {session?.job_role ? ` · ${session.job_role}` : ''}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 220 }}>
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1.5}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading feedback…
            </Typography>
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && !data && (
          <Box textAlign="center" py={6}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              No feedback submitted for this interview.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              The candidate did not submit post-interview feedback.
            </Typography>
          </Box>
        )}

        {!loading && !error && data?.is_skipped && (
          <Box textAlign="center" py={5}>
            <SkipIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="body2" fontWeight={700}>
              Candidate skipped feedback
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              The candidate chose to skip the optional questionnaire.
            </Typography>
            {submittedLabel && (
              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                {submittedLabel}
              </Typography>
            )}
          </Box>
        )}

        {!loading && !error && data && !data.is_skipped && (
          <Stack spacing={2.5}>
            {data.questionnaire?.title && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {data.questionnaire.title}
                </Typography>
                {data.questionnaire.description && (
                  <Typography variant="body2" color="text.secondary">
                    {data.questionnaire.description}
                  </Typography>
                )}
              </Box>
            )}

            <Stack spacing={1.5}>
              {(data.questionnaire?.questions || []).map((q) => {
                const answer = data.responses?.[q.id];
                return (
                  <Box
                    key={q.id}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} mb={1}>
                      {q.question_text}
                    </Typography>

                    {q.question_type === 'rating' ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StarRating score={answer ? Number(answer) : 0} />
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={`${answer || 0}/5`}
                        />
                      </Stack>
                    ) : q.question_type === 'single_choice' || q.question_type === 'multiple_choice' ? (
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {(Array.isArray(answer) ? answer : [answer]).filter(Boolean).map((opt, i) => (
                          <Chip
                            key={`${q.id}-${i}`}
                            size="small"
                            color="success"
                            variant="outlined"
                            label={String(opt)}
                          />
                        ))}
                        {(!answer || (Array.isArray(answer) && answer.length === 0)) && (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            No answer provided
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.primary">
                        {answer != null && String(answer).trim() !== '' ? (
                          String(answer)
                        ) : (
                          <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            No answer provided
                          </Box>
                        )}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>

            {submittedLabel && (
              <Typography variant="caption" color="text.secondary" textAlign="right">
                Submitted {submittedLabel}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SessionFeedbackDialog;
